import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";


const LOT_CATEGORIES = {
  "Fabrication": "Lot 1",
  "Encadrement": "Lot 1",
  "Autre (technique)": "Lot 1",
  "Exposition": "Lot 2",
  "Documentation": "Lot 2",
  "Frais juridiques": "Lot 2",
  "Autre (mise en valeur)": "Lot 2"
};

export default function ArtworkCosts() {
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  const typeLabels = {
    "Fabrication": t("artworkCosts.types.fabrication"),
    "Encadrement": t("artworkCosts.types.framing"),
    "Autre (technique)": t("artworkCosts.types.otherTechnical"),
    "Exposition": t("artworkCosts.types.exhibition"),
    "Documentation": t("artworkCosts.types.documentation"),
    "Frais juridiques": t("artworkCosts.types.legalFees"),
    "Autre (mise en valeur)": t("artworkCosts.types.otherEnhancement"),
  };
  const lotLabel = (lot) => lot === "Lot 1" ? t("artworkCosts.lots.lot1") : lot === "Lot 2" ? t("artworkCosts.lots.lot2") : lot;
  const formatMoney = (value) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(Number(value || 0));
  const { artworkId } = useParams();
  const [artworkTitle, setArtworkTitle] = useState(null);
  const [costs, setCosts] = useState([]);
  const [newCost, setNewCost] = useState({
    type: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    prestataire: "",
    lot: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchCosts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkId]);

  const fetchCosts = async () => {
    const { data, error } = await supabase
      .from("artwork_costs")
      .select("*")
      .eq("artwork_id", artworkId);

    if (error) {
      console.error("Erreur lors du chargement des coûts:", error);
    } else {
      setCosts(data);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedCost = { ...newCost, [name]: value };

    if (name === "type") {
      const lot = LOT_CATEGORIES[value] || "";
      updatedCost.lot = lot;
    }

    setNewCost(updatedCost);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const { error } = await supabase.from("artwork_costs").insert({
      ...newCost,
      amount: parseFloat(newCost.amount),
      artwork_id: artworkId
    });

    if (error) {
      console.error("Erreur lors de l'ajout du coût:", error);
      alert(t("artworkCosts.saveError"));
    } else {
      setNewCost({ type: "", amount: "", date: new Date().toISOString().split("T")[0], description: "", prestataire: "", lot: "" });
      fetchCosts();
    }
    setIsSubmitting(false);
  };

  const total = costs.reduce((sum, cost) => sum + (cost.amount || 0), 0);
  useEffect(() => {
    const fetchArtworkTitle = async () => {
      const { data, error } = await supabase
        .from("artworks")
        .select("title")
        .eq("id", artworkId)
        .single();

      if (!error && data) {
        setArtworkTitle(data.title || null);
      }
    };

    if (artworkId) {
      fetchArtworkTitle();
    }
  }, [artworkId]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
      <h1 className="text-2xl font-semibold mb-6">
        {t("artworkCosts.title", { artwork: artworkTitle || t("artworkCosts.artworkFallback", { id: artworkId }) })}
      </h1>
        <Link to={`/artwork/${artworkId}`} className="btn-artsy btn-secondary-artsy">{t("artworkCosts.back")}</Link>
      </div>

      <div className="bg-white shadow rounded p-4 mb-6">
        <h2 className="text-lg font-medium mb-2">{t("artworkCosts.addTitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <select name="type" value={newCost.type} onChange={handleChange} className="border p-2 rounded">
            <option value="">{t("artworkCosts.selectType")}</option>
            <option value="Fabrication">{typeLabels["Fabrication"]}</option>
            <option value="Encadrement">{typeLabels["Encadrement"]}</option>
            <option value="Autre (technique)">{typeLabels["Autre (technique)"]}</option>
            <option value="Exposition">{typeLabels["Exposition"]}</option>
            <option value="Documentation">{typeLabels["Documentation"]}</option>
            <option value="Frais juridiques">{typeLabels["Frais juridiques"]}</option>
            <option value="Autre (mise en valeur)">{typeLabels["Autre (mise en valeur)"]}</option>
          </select>
          <input name="amount" value={newCost.amount} onChange={handleChange} placeholder={t("artworkCosts.amountPlaceholder")} type="number" className="border p-2 rounded" />
          <input name="date" value={newCost.date} onChange={handleChange} type="date" className="border p-2 rounded" />
          <input name="description" value={newCost.description} onChange={handleChange} placeholder={t("artworkCosts.description")} className="border p-2 rounded" />
          <input name="prestataire" value={newCost.prestataire} onChange={handleChange} placeholder={t("artworkCosts.provider")} className="border p-2 rounded" />
        </div>
        {newCost.lot && <p className="text-sm text-gray-600 mt-2">{t("artworkCosts.automaticCategory")} <strong>{lotLabel(newCost.lot)}</strong></p>}
        <button 
          onClick={handleSubmit} 
          disabled={isSubmitting}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {isSubmitting ? t("artworkCosts.saving") : t("artworkCosts.add")}
        </button>
      </div>

      <div className="bg-white shadow rounded p-4">
        <h2 className="text-lg font-medium mb-4">{t("artworkCosts.history")}</h2>
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2 text-left">{t("artworkCosts.columns.date")}</th>
              <th className="border p-2 text-left">{t("artworkCosts.columns.type")}</th>
              <th className="border p-2 text-left">{t("artworkCosts.columns.amount")}</th>
              <th className="border p-2 text-left">{t("artworkCosts.columns.lot")}</th>
              <th className="border p-2 text-left">{t("artworkCosts.columns.description")}</th>
              <th className="border p-2 text-left">{t("artworkCosts.columns.provider")}</th>
            </tr>
          </thead>
          <tbody>
            {costs.map((cost) => (
              <tr key={cost.id}>
                <td className="border p-2">{new Intl.DateTimeFormat(locale).format(new Date(cost.date))}</td>
                <td className="border p-2">{typeLabels[cost.type] || cost.type}</td>
                <td className="border p-2">{formatMoney(cost.amount)}</td>
                <td className="border p-2">{lotLabel(cost.lot) || "-"}</td>
                <td className="border p-2">{cost.description}</td>
                <td className="border p-2">{cost.prestataire || "-"}</td>
              </tr>
            ))}
            <tr className="font-bold">
              <td className="border p-2" colSpan={2}>{t("artworkCosts.total")}</td>
              <td className="border p-2">{formatMoney(total)}</td>
              <td className="border p-2"></td>
              <td className="border p-2"></td>
              <td className="border p-2"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
